import React, { useState, useEffect } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import Loader from "views/universal/Loader/Loader";
import { Col, Row } from "react-bootstrap";
import swal from 'sweetalert';
import moment from "moment";
import { Stepper, Step, StepLabel, StepContent, Paper, CircularProgress, Button } from '@material-ui/core';
import { savOperations } from "state/ducks/store/sav";
import { orderOperations } from "state/ducks/store/order";
import Filters from "./Filters/Filters";
import { formatOrderedBefore, isOrderedBeforeIncomplete } from "../Common/OrderedBeforeFilter";
import Variables from "./Variables";
import Orders from "./Orders"
import TypeFilter from "./Filters/TypeFilter"
import ResponseCard from "../Common/ResponseCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield, faPaperPlane, faMobileAlt, faHourglassHalf, faGift, faCheck, faTimes, faArrowRight } from "@fortawesome/free-solid-svg-icons";

const WorkflowOverview = () => {
  const stepStyle = {
    flex: '1 1 0',
    minWidth: 0,
    background: '#fff',
    border: '1px solid #e3e3e3',
    borderRadius: 8,
    padding: '8px 10px',
    textAlign: 'center',
    fontSize: '0.78rem',
    lineHeight: 1.2,
  };
  const arrowStyle = {
    color: '#abd156',
    fontSize: '0.9rem',
    flexShrink: 0,
    alignSelf: 'center',
  };
  const iconStyle = (color) => ({
    color,
    fontSize: '1.2rem',
    marginBottom: 4,
  });
  return (
    <Paper square elevation={0} className="mt-2 p-3" style={{ background: '#f5f9f0', border: '1px dashed #abd156' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, color: '#3d6e2a' }}>
        🔄 Workflow d'une proposition SAV — pour comprendre ce qui se passe après votre clic "Créer les propositions SAV" :
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, flexWrap: 'wrap' }}>
        <div style={stepStyle}>
          <div><FontAwesomeIcon icon={faUserShield} style={iconStyle('#3d6e2a')} /></div>
          <strong>1. Vous créez</strong>
          <div style={{ color: '#666' }}>cagnotte + options produit + motif</div>
        </div>
        <FontAwesomeIcon icon={faArrowRight} style={arrowStyle} />
        <div style={stepStyle}>
          <div><FontAwesomeIcon icon={faPaperPlane} style={iconStyle('#3d6e2a')} /></div>
          <strong>2. Envoi auto</strong>
          <div style={{ color: '#666' }}>SMS + email avec lien unique</div>
        </div>
        <FontAwesomeIcon icon={faArrowRight} style={arrowStyle} />
        <div style={stepStyle}>
          <div><FontAwesomeIcon icon={faMobileAlt} style={iconStyle('#3d6e2a')} /></div>
          <strong>3. Client choisit</strong>
          <div style={{ color: '#666' }}>parmi vos options, ou refuse / recontact</div>
        </div>
        <FontAwesomeIcon icon={faArrowRight} style={arrowStyle} />
        <div style={stepStyle}>
          <div><FontAwesomeIcon icon={faGift} style={iconStyle('#3d6e2a')} /></div>
          <strong>4. Avantage créé</strong>
          <div style={{ color: '#666' }}>utilisable au panier + email récap</div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: '#666', flexWrap: 'wrap' }}>
        <FontAwesomeIcon icon={faHourglassHalf} style={{ color: '#fbc02d' }} />
        <strong style={{ color: '#3d6e2a' }}>Pas de réponse sous 48h ?</strong>
        <span>l'option la plus avantageuse (plafond max) est appliquée automatiquement → le client reçoit un email "votre dédommagement a été appliqué".</span>
      </div>
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.78rem', color: '#666', flexWrap: 'wrap' }}>
        <span><FontAwesomeIcon icon={faCheck} style={{ color: '#abd156' }} /> Accepte : avantage utilisable au prochain panier</span>
        <span><FontAwesomeIcon icon={faTimes} style={{ color: '#999' }} /> Refuse : cagnotte créditée d'office reste (ne touche pas le client), aucun produit offert</span>
      </div>
    </Paper>
  );
};

const extractCreditedAmountFromDescription = (description = '') => {
  const match = description.match(/(-?\d+(?:[.,]\d+)?)\s*€/);
  if (!match) {
    return 0;
  }

  const numericAmount = parseFloat(match[1].replace(',', '.'));
  return Number.isNaN(numericAmount) ? 0 : numericAmount;
};

const getOrderCustomerId = (order) => (
  order?.customerInfo?.customer?.id
  || order?.customer_info?.customer?.id
  || order?.customer_info?.id
  || order?.customer_id
  || order?.customerId
  || null
);

const getOrderCustomerEmail = (order) => (
  order?.customerInfo?.customer?.email
  || order?.customer_info?.customer?.email
  || order?.customer_info?.email
  || order?.customerInfo?.email
  || null
);

const getCustomerEmailFromSelectOption = (option, customers = []) => {
  if (!option) {
    return null;
  }
  const fromList = customers.find((c) => String(c.id) === String(option.value));
  if (fromList?.email) {
    return fromList.email;
  }
  if (option.label) {
    const parts = option.label.split(' - ');
    if (parts.length > 1) {
      return parts[parts.length - 1].trim();
    }
  }
  return null;
};

const formatTargetMailLabel = (target) => target.customerEmail || `Client #${target.customerId}`;

const CreditPool = (props) => {

  const savPage = useSelector(
    (state) => state.store.sav.admin,
    shallowEqual
  );

  const productCategories = useSelector(
    (state) => state.store.sav.propositions.categories,
    shallowEqual
  );

  const [requestParams, setRequestParams] = useState({
    rangeDates      : {start: moment(), end: moment()},
    orderedBefore   : {date: null, time: ''},
    freeCreditAmount: 0,
    note            : '',
    savCategory     : '',
    vigilanceLevel  : '',
    addCreditOrderAmount: false,
    addSupercookieCode: false,
    creditOrderAmountPercentage: 100
  });

  const [filterSelected, setFilterSelected] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse]   = useState(null); // {'total', 'success', 'fails'}
  // Permet de gérer l'état des étapes
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState([]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!productCategories || productCategories.length === 0) {
      dispatch(savOperations.listPropositionCategories());
    }
  }, [dispatch]);

  /**
   * Gère le changement des variables
   */
  const handleFiltersChange = (filters) => {
    setFilterSelected(filters);
  }

  /**
   * Gère la sélection des commandes
   */
  const handleSelectionChange = (selectedRows) => {
    setSelectedOrders(selectedRows);
  };

  const [freeCreditAmount, setFreeCreditAmount]         = useState(0);
  const [note, setNote]                                 = useState("");
  const [savCategory, setSavCategory]                   = useState("");
  const [vigilanceLevel, setVigilanceLevel]             = useState("");
  const [addCreditOrderAmount, setAddCreditOrderAmount] = useState(false);
  const [addSupercookieCode, setAddSupercookieCode]     = useState(false);
  const [creditOrderAmountPercentage, setCreditOrderAmountPercentage] = useState(100);
  const [productOptions, setProductOptions]             = useState([]);

  const resetCreditOptions = () => {
    setRequestParams({
      ...requestParams,
      freeCreditAmount: 0,
      note: '',
      savCategory: '',
      vigilanceLevel: '',
      addCreditOrderAmount: false,
      addSupercookieCode: false,
      creditOrderAmountPercentage: 100,
    });
    setFreeCreditAmount(0);
    setNote('');
    setSavCategory('');
    setVigilanceLevel('');
    setAddCreditOrderAmount(false);
    setAddSupercookieCode(false);
    setCreditOrderAmountPercentage(100);
    setProductOptions([]);
  };

  const handleChange = (name, value) => {
    handleVariablesChange(name, value);
    switch (name) {
      case 'freeCreditAmount':
        setFreeCreditAmount(value);
        break;
      case 'note':
        setNote(value);
        break;
      case 'savCategory':
        setSavCategory(value);
        break;
      case 'vigilanceLevel':
        setVigilanceLevel(value);
        break;
      case 'addCreditOrderAmount':
        setAddCreditOrderAmount(value);
        break;
      case 'addSupercookieCode':
        setAddSupercookieCode(value);
        break;
      case 'creditOrderAmountPercentage':
        setCreditOrderAmountPercentage(value);
        break;
      case 'productOptions':
        setProductOptions(value);
        break;
      default:
        break;
    }
  };
  
  /**
   * Gère le changement des variables
   */
  const handleVariablesChange = (attr, value) => {
    setRequestParams({
      ...requestParams,
      [attr]:value
    });
  }

  const requestParamsOk = () => {
    if (
      (requestParams.freeCreditAmount <= 0 && !requestParams.addCreditOrderAmount && !requestParams.addSupercookieCode) ||
      requestParams.note === '' ||
      !requestParams.savCategory ||
      (selectedOrders.length <= 0 && typeFilter === 'order') || 
      (filterSelected.customers && filterSelected.customers.length <= 0 && typeFilter === 'customer')
    ) {
      return false;
    }
    return true;
  }

  /**
   * Récupère les commandes
   */
  const getOrders = () => {
    setIsLoading(true);

    let params = {...requestParams}
    params = {
      ...params,
      deliveryType  : filterSelected.deliveryType && filterSelected.deliveryType.value,
      startDate     : params.rangeDates && params.rangeDates.start && moment(params.rangeDates.start).format("YYYY-MM-DD"),
      endDate       : params.rangeDates && params.rangeDates.end && moment(params.rangeDates.end).format("YYYY-MM-DD"),
      orderedBefore : formatOrderedBefore(params.orderedBefore),
      customers     : filterSelected.customers && filterSelected.customers.map((value) => value.value),
      vehicles      : filterSelected.vehicles && filterSelected.vehicles.map((value)   => value.value),
      spots         : filterSelected.spots && filterSelected.spots.map((value)         => value.value),
      excludedSpots : filterSelected.excludedSpots && filterSelected.excludedSpots.map((value) => value.value),
      establishment : filterSelected.establishment && filterSelected.establishment.value,
      products : filterSelected.products && filterSelected.products.filter((value) => {
        let type = value.value.split('#')
        return (type.length === 2 && type[1] === 'product');
      }).map((value) => parseInt(value.value.split('#')[0])),
      ingredients : filterSelected.products && filterSelected.products.filter((value) => {
        let type = value.value.split('#')
        return (type.length === 2 && type[1] === 'ingredient');
      }).map((value) => parseInt(value.value.split('#')[0])),
      groups: ['order_info', 'order_multiple']
    };

    return dispatch(orderOperations.getOrders(params))
    .then(
      (result) => {
        setIsLoading(false);
        if (result.data && result.data.result) {
          setResponse(result.data.result);
          return true;
        }
        return false;
      },
      (error) => {
        setIsLoading(false);
        if (error.data && error.data.result) {
          setResponse(error.data.result);
        }
        return false;
      }
    )
  }

  /**
   * Crédite les porte-monnaie numérique (nommé anciennement cagnotte)
   */
  const creditPools = async () => {
    if (!requestParamsOk()) {
      swal({
        title: "Pas si vite !",
        text: "Merci de renseigner une compensation, un motif, une catégorie et au moins une commande",
        icon: "warning",
        buttons: {
          ok: "Ok",
        },
      })
      return false;
    }

    let popupTxt = "";

    // Détermine le texte de base en fonction de freeCreditAmount
    if (requestParams.freeCreditAmount > 0) {
      if (typeFilter === 'order') {
        popupTxt = `Etes-vous sûr de vouloir créditer de ${requestParams.freeCreditAmount}€ les cagnottes clients liées à ces commandes ?`;
      } else {
        popupTxt = `Etes-vous sûr de vouloir créditer de ${requestParams.freeCreditAmount}€ les cagnottes de ces clients ?`;
      }
    }
    
    // Ajoute des détails supplémentaires sans "également" si freeCreditAmount est 0
    const percentage = requestParams.creditOrderAmountPercentage || 100;
    if (addCreditOrderAmount && addSupercookieCode) {
      popupTxt += requestParams.freeCreditAmount > 0 
        ? ` Vous allez également créditer ${percentage}% du montant de la commande sur la cagnotte et offrir un code supercookie.`
        : ` Vous allez créditer ${percentage}% du montant de la commande sur la cagnotte et offrir un code supercookie.`;
    } else if (addCreditOrderAmount) {
      popupTxt += requestParams.freeCreditAmount > 0 
        ? ` Vous allez également créditer ${percentage}% du montant de la commande sur la cagnotte.`
        : ` Vous allez créditer ${percentage}% du montant de la commande sur la cagnotte.`;
    } else if (addSupercookieCode) {
      popupTxt += requestParams.freeCreditAmount > 0 
        ? " Vous allez également offrir un code supercookie."
        : " Vous allez offrir un code supercookie.";
    }

    return swal({
      title: "Créditer les cagnottes ?",
      text: popupTxt,
      icon: "warning",
      buttons: {
        cancel: "Annuler",
        confirm: "Valider",
      },
    })
    .then((confirm) => {
      if (confirm === true) {
        setIsLoading(true);
        let params = {...requestParams}
        if (typeFilter === 'order') {
          params = {
            ...params,
            deliveryType    : filterSelected.deliveryType && filterSelected.deliveryType.value,
            startDate       : params.rangeDates && params.rangeDates.start && moment(params.rangeDates.start).format("YYYY-MM-DD"),
            endDate         : params.rangeDates && params.rangeDates.end && moment(params.rangeDates.end).format("YYYY-MM-DD"),
            orderedBefore   : formatOrderedBefore(params.orderedBefore),
            freeCreditAmount: parseInt(params.freeCreditAmount * 100),
            customers       : filterSelected.customers && filterSelected.customers.map((value) => value.value),
            vehicles        : filterSelected.vehicles && filterSelected.vehicles.map((value) => value.value),
            spots           : filterSelected.spots && filterSelected.spots.map((value) => value.value),
            excludedSpots   : filterSelected.excludedSpots && filterSelected.excludedSpots.map((value) => value.value),
            establishment   : filterSelected.establishment && filterSelected.establishment.value,
            products : filterSelected.products && filterSelected.products.filter((value) => {
              let type = value.value.split('#')
              return (type.length === 2 && type[1] === 'product');
            }).map((value) => parseInt(value.value.split('#')[0])),
            ingredients : filterSelected.products && filterSelected.products.filter((value) => {
              let type = value.value.split('#')
              return (type.length === 2 && type[1] === 'ingredient');
            }).map((value) => parseInt(value.value.split('#')[0])),
            groups: ['order_info', 'order_multiple'],
            orderIds: selectedOrders,
            addCreditOrderAmount: addCreditOrderAmount,
            addSupercookieCode: addSupercookieCode,
            creditOrderAmountPercentage: requestParams.creditOrderAmountPercentage || 100
          };
        }
        else if (typeFilter === 'customer') {
          params = {
            ...params,
            customerIds: filterSelected.customers && filterSelected.customers.map((value) => value.value),
          };
          params = {
            ...params,
            freeCreditAmount: parseInt(params.freeCreditAmount * 100),
          };
        }

        return dispatch(savOperations.creditPools(typeFilter, params))
        .then(
          (result) => {
            setIsLoading(false);
            if (result.data && result.data.result) {
              setResponse(result.data.result);
              // Réinitialise les options après un crédit réussi
              resetCreditOptions();
            }
            return true;
          },
          (error) => {
            setIsLoading(false);
            if (error.data && error.data.result) {
              setResponse(error.data.result);
            }
            return false;
          }
        )
      }
    });
  }

  /**
   * Mode brique : crée une SavProposition par commande (typeFilter=order) ou par
   * client (typeFilter=customer). Chaque proposition applique d'office la cagnotte
   * (montant libre + % commande) et peut proposer un dédommagement produit au choix.
   */
  const createPropositions = async () => {
    if (note === '') {
      swal({ title: "Motif requis", text: "Indiquez le motif du SAV", icon: "warning" });
      return false;
    }
    if (!savCategory) {
      swal({ title: "Catégorie requise", text: "Indiquez la catégorie du SAV", icon: "warning" });
      return false;
    }
    if (freeCreditAmount <= 0 && !addCreditOrderAmount && (!productOptions || productOptions.length === 0)) {
      swal({ title: "Proposition vide", text: "Ajoutez un montant cagnotte ou une option produit", icon: "warning" });
      return false;
    }

    // Collecte les cibles (orderIds + customerIds).
    let targets = [];
    if (typeFilter === 'order') {
      if (!selectedOrders || selectedOrders.length === 0) {
        swal({ title: "Aucune commande", text: "Sélectionnez au moins une commande", icon: "warning" });
        return false;
      }
      const orderMap = {};
      (response?.orders || []).forEach((o) => {
        orderMap[o.id] = {
          customerId: getOrderCustomerId(o),
          customerEmail: getOrderCustomerEmail(o),
        };
      });
      targets = selectedOrders
        .map((orderId) => ({ orderId, ...orderMap[orderId] }))
        .filter((t) => t.customerId);
    } else if (typeFilter === 'customer') {
      const customers = filterSelected.customers || [];
      if (customers.length === 0) {
        swal({ title: "Aucun client", text: "Sélectionnez au moins un client", icon: "warning" });
        return false;
      }
      targets = customers.map((c) => ({
        orderId: null,
        customerId: c.value,
        customerEmail: getCustomerEmailFromSelectOption(c, savPage?.customers || []),
      }));
    }

    if (targets.length === 0) {
      swal({ title: "Aucune cible valide", text: "Impossible de récupérer les clients", icon: "warning" });
      return false;
    }

    const cagnottePercentForRecap = addCreditOrderAmount && typeFilter === 'order'
      ? (creditOrderAmountPercentage || 0)
      : 0;
    const recapLines = [
      `${targets.length} proposition(s) seront créées et envoyées au(x) client(s) (SMS/email).`,
      '',
      '--- RÉCAPITULATIF ---',
    ];
    if (freeCreditAmount > 0 || cagnottePercentForRecap > 0) {
      recapLines.push('Cagnotte appliquée d\'office :');
      if (freeCreditAmount > 0) {
        // Le champ garde la saisie brute (chaîne) pour accepter les centimes.
        recapLines.push(`  • ${Number(freeCreditAmount).toFixed(2)} € fixe`);
      }
      if (cagnottePercentForRecap > 0) {
        recapLines.push(`  • ${cagnottePercentForRecap}% du total commande`);
      }
    } else {
      recapLines.push('Cagnotte : aucune');
    }
    if (productOptions && productOptions.length > 0) {
      recapLines.push('Dédommagement produit (au choix du client) :');
      productOptions.forEach((opt) => {
        const cat = (productCategories || []).find((c) => c.key === opt.category);
        const label = cat?.label || opt.category;
        const max = parseFloat(opt.maxAmount || 0);
        recapLines.push(`  • ${label}${max > 0 ? ` (plafond ${max.toFixed(2)} €)` : ''}`);
      });
    } else {
      recapLines.push('Dédommagement produit : aucun');
    }
    recapLines.push('');
    recapLines.push(`Catégorie : ${(savPage?.savCategories || {})[savCategory] || savCategory}`);
    if (vigilanceLevel) {
      recapLines.push(`Vigilance client : ${(savPage?.vigilanceLevels || {})[vigilanceLevel] || vigilanceLevel}`);
    }
    recapLines.push(`Motif : ${note}`);

    const confirm = await swal({
      title: "Créer les propositions SAV ?",
      text: recapLines.join('\n'),
      icon: "warning",
      buttons: { cancel: "Annuler", confirm: "Valider" },
    });
    if (confirm !== true) return false;

    setIsLoading(true);
    const cagnotteFixedCentimes = freeCreditAmount > 0 ? Math.round(freeCreditAmount * 100) : null;
    const cagnottePercent = addCreditOrderAmount && typeFilter === 'order'
      ? (creditOrderAmountPercentage || 0)
      : null;
    // Convertit les options produit en centimes pour l'API.
    const apiOptions = (productOptions || []).map(o => ({
      category: o.category,
      maxAmount: Math.round(parseFloat(o.maxAmount || 0) * 100),
    }));

    const results = { success: [], fails: [] };
    for (const t of targets) {
      try {
        await dispatch(savOperations.createProposition({
          customerId: t.customerId,
          orderId: t.orderId,
          reason: note,
          savCategory: savCategory,
          vigilanceLevel: vigilanceLevel || null,
          cagnotteFixedAmount: cagnotteFixedCentimes,
          cagnottePercent: cagnottePercent,
          productOptions: apiOptions,
        }));
        results.success.push(t);
      } catch (e) {
        results.fails.push({ ...t, error: e?.message || 'erreur' });
      }
    }

    setIsLoading(false);
    setResponse({
      total: targets.length,
      success: results.success.map((t) => ({
        mail: formatTargetMailLabel(t),
        description: t.orderId ? `Commande #${t.orderId}` : 'Proposition créée',
      })),
      fails: results.fails.map((t) => ({
        mail: formatTargetMailLabel(t),
        description: t.error || 'Erreur',
      })),
      propositionSummary: {
        successCount: results.success.length,
        cagnotteFixed: freeCreditAmount > 0 ? Number(freeCreditAmount) : 0,
        cagnottePercent: cagnottePercent || 0,
        productOptions: (productOptions || []).map((o) => ({
          category: o.category,
          maxAmount: parseFloat(o.maxAmount || 0),
        })),
        reason: note,
      },
    });
    resetCreditOptions();
    return true;
  };

  /**
   * Gère l'état du type de recherche voulu
   */
  const [typeFilter, setTypeFilter]   = useState('order');
  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
  }

  /**
   * Permet de définir les labels des Steppers
   */
  const getSteps = () => {
    return ['Choisir le type de recherche', 'Faite une recherche par commande ou client', 'Vérifier la liste des commandes éligibles', 'Choisir une proposition de SAV'];
  }

  /**
   * Permet de définir les composants utilisés pour telle Step
   */
  const getStepContent = (step) => {
    let comp;
    switch (step) {
      case 0:
        comp = <TypeFilter value={typeFilter} handleChange={handleTypeFilterChange}/>;
        break;
      case 1:
        comp = <Filters
          typeFilter={typeFilter}
          handleChange={handleFiltersChange}
          vehicles={savPage.vehicles}
          spots={savPage.spots}
          establishments={savPage.establishments}
          products={savPage.products}
          customers={savPage.customers}
          handleDateChange={handleVariablesChange}
          rangeDates={requestParams.rangeDates}
          orderedBefore={requestParams.orderedBefore}
          />
        ;
        break;
      case 2:
        comp = <Orders orders={(response && response.orders) ? response.orders : []} onSelectionChange={handleSelectionChange}/>;
        break;
      case 3:
        comp =
          <Variables
            typeFilter={typeFilter}
            freeCreditAmount={freeCreditAmount}
            note={note}
            savCategory={savCategory}
            savCategories={savPage.savCategories}
            savCategoriesGrouped={savPage.savCategoriesGrouped}
            vigilanceLevel={vigilanceLevel}
            vigilanceLevels={savPage.vigilanceLevels}
            handleChange={handleChange}
            addCreditOrderAmount={addCreditOrderAmount}
            creditOrderAmountPercentage={creditOrderAmountPercentage}
            productOptions={productOptions}
            productCategories={productCategories}
          />;
          break;
      default:
        comp = 'Etape inconnu';
        break;
    }
    return comp;
  }

  /**
   * Permet de définir quelle fonction est utilisée lors du passage à l'étape suivante
   */
  const getStepFunction = async (step) => {
    switch (step) {
      case 0:
        handleNext();
        break;
      case 1:
        if (typeFilter === 'order') {
          await getOrders();
        }
        handleNext();
        break;
      case 2:
        handleNext();
        break;
      case 3:
        const ok = await createPropositions();
        if (ok) {
          handleNext();
        }
        break;
      default:
        break;
    }
  }

  const steps = getSteps();

  const handleNext = () => {
    let plus = 1;
    if (typeFilter === 'customer') {
      if (activeStep + 1 === 2) {
        plus = 2;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + plus);
  };

  const handleBack = () => {
    let minus = 1;
    if (typeFilter === 'customer') {
      if (activeStep - 1 === 2) {
        minus = 2;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep - minus);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  // Si le bouton suivant doit être désactivé
  const isNextDisable = () => {
    if (activeStep === 1) {
      if (requestParams.rangeDates.start === null || requestParams.rangeDates.end === null) {
        return true;
      }
      // Filtre "commandé avant" à moitié renseigné (date sans heure ou l'inverse)
      if (isOrderedBeforeIncomplete(requestParams.orderedBefore)) {
        return true;
      }
      // Maximum 30 jours
      if (requestParams.rangeDates.start && requestParams.rangeDates.end) {
          let diff = moment(requestParams.rangeDates.start).diff(requestParams.rangeDates.end, 'days');
          if (diff > 30) {
            return false;
          }
      }
    }
    if (activeStep === 2 && (response === null || response.orders === undefined || response?.orders.length === 0)) {
      return true;
    }
    if (typeFilter === 'customer') {
      if (activeStep === 1) {
        if (!filterSelected?.customers || filterSelected.customers.length <= 0) {
          return true;
        }
      }
    }

    return false;
  }

  const creditedAmounts = (response?.success || [])
    .filter((item) => typeof item.description === "string" && item.description.includes("cagnott"))
    .map((item) => extractCreditedAmountFromDescription(item.description));

  const totalCreditedAmount = creditedAmounts.reduce((sum, amount) => sum + amount, 0);
  const hasCreditSummary = creditedAmounts.length > 0 && totalCreditedAmount > 0;

  const propositionSummary = response?.propositionSummary;
  const hasPropositionSummary = propositionSummary && propositionSummary.successCount > 0;
  const propositionCategoryLabel = (key) => {
    const found = (productCategories || []).find((c) => c.key === key);
    return found?.label || key;
  };

  return (
    <>
      {savPage.isLoading ? (
        <Loader items={2} />
      ) : (
        <>
          <WorkflowOverview />
          <div className="mt-3">
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {getStepContent(index)}
                    <Row>
                      <Col xs={12}>
                        <Button
                          disabled={activeStep === 0}
                          onClick={handleBack}
                        >
                          Retour
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={isNextDisable() || isLoading}
                          onClick={(e) => getStepFunction(index)}
                        >
                          {isLoading
                            ? <CircularProgress color="inherit" size={18} />
                            : activeStep === steps.length - 1
                              ? 'Créer les propositions SAV'
                              : 'Suivant'
                          }
                        </Button>
                      </Col>
                    </Row>
                  </StepContent>
                </Step>
              ))}
              {activeStep === steps.length && (
                <Paper square elevation={0}>
                  <Row className="mt-3">
                    <Col xs={4}>
                      <div className="mt-3">
                        <Button onClick={handleReset} variant="outlined" color="primary">
                          Faire un autre crédit ?
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Paper>
              )}
              <ResponseCard title={"Résultats de la dernière requête"} success={response?.success} fails={response?.fails}/>
              {hasCreditSummary && (
                <Paper square elevation={0} className="mt-2 p-3">
                  <strong>Montant total crédité</strong>
                  <div>{totalCreditedAmount.toFixed(2)} € (montant fixe + pourcentage commande)</div>
                  <div className="text-muted">Ce total ne comprend pas les codes promo / produits offerts.</div>
                </Paper>
              )}
              {hasPropositionSummary && (
                <Paper square elevation={0} className="mt-2 p-3">
                  <strong>Récapitulatif des propositions envoyées</strong>
                  <div className="mt-2">
                    <strong>{propositionSummary.successCount}</strong> proposition(s) envoyée(s) au(x) client(s)
                  </div>
                  {(propositionSummary.cagnotteFixed > 0 || propositionSummary.cagnottePercent > 0) && (
                    <div className="mt-2">
                      <strong>Montant remboursé en cagnotte (par proposition) :</strong>
                      <ul className="mb-0">
                        {propositionSummary.cagnotteFixed > 0 && (
                          <li>{propositionSummary.cagnotteFixed.toFixed(2)} € fixe</li>
                        )}
                        {propositionSummary.cagnottePercent > 0 && (
                          <li>{propositionSummary.cagnottePercent}% du total commande</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {propositionSummary.productOptions && propositionSummary.productOptions.length > 0 ? (
                    <div className="mt-2">
                      <strong>Compensation produit proposée :</strong>
                      <ul className="mb-0">
                        {propositionSummary.productOptions.map((opt, i) => (
                          <li key={i}>
                            {propositionCategoryLabel(opt.category)}
                            {opt.maxAmount > 0 ? ` (plafond ${opt.maxAmount.toFixed(2)} €)` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-2 text-muted">Aucune compensation produit proposée</div>
                  )}
                  {propositionSummary.reason && (
                    <div className="mt-2 text-muted">
                      <em>Motif : {propositionSummary.reason}</em>
                    </div>
                  )}
                </Paper>
              )}
            </Stepper>
          </div>
        </>
      )}
    </>
  );
};

CreditPool.propTypes = {};

CreditPool.defaultProps = {};

export default CreditPool;
